import React, { useEffect, useState } from 'react';
import { Cookie, ShieldCheck, SlidersHorizontal, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'mce_cookie_consent';
const CONSENT_VERSION = 2;
const CONSENT_MAX_AGE = 180 * 24 * 60 * 60;

const readConsent = () => {
  try {
    let raw;
    try { raw = window.localStorage.getItem(STORAGE_KEY); } catch { /* Fall back to the consent cookie. */ }
    if (!raw) raw = decodeURIComponent(document.cookie.split('; ').find(value => value.startsWith(STORAGE_KEY + '='))?.slice(STORAGE_KEY.length + 1) || 'null');
    const saved = JSON.parse(raw);
    const age = Date.now() - Date.parse(saved?.savedAt);
    return saved?.version === CONSENT_VERSION && age >= 0 && age < CONSENT_MAX_AGE * 1000
      && ['analytics', 'marketing', 'personalization'].every(key => typeof saved[key] === 'boolean') ? saved : null;
  } catch {
    return null;
  }
};

const persistConsent = (preferences) => {
  const consent = { version: CONSENT_VERSION, essential: true, session: true, analytics: preferences.analytics === true, marketing: preferences.marketing === true, personalization: preferences.personalization === true, savedAt: new Date().toISOString() };
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent)); } catch { /* Cookies remain available when local storage is blocked. */ }
  try {
    document.cookie = `${STORAGE_KEY}=${encodeURIComponent(JSON.stringify(consent))}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch { /* Keep the choice for the current page if cookie storage is blocked. */ }
  window.mceCookieConsent = consent;
  window.dispatchEvent(new CustomEvent('mce:consent-change', { detail: consent }));
  window.dataLayer?.push({
    event: 'mce_consent_update',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    personalization_storage: consent.personalization ? 'granted' : 'denied',
  });
  return consent;
};

export default function CookieConsent() {
  const [consent, setConsent] = useState(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState({ analytics: false, marketing: false, personalization: false });

  useEffect(() => {
    if (consent) {
      window.mceCookieConsent = consent;
      window.dispatchEvent(new CustomEvent('mce:consent-change', { detail: consent }));
      setPreferences({ analytics: !!consent.analytics, marketing: !!consent.marketing, personalization: !!consent.personalization });
    }
    const openSettings = () => {
      const current = readConsent();
      setPreferences({ analytics: !!current?.analytics, marketing: !!current?.marketing, personalization: !!current?.personalization });
      setSettingsOpen(true);
    };
    window.addEventListener('mce:open-cookie-settings', openSettings);
    return () => window.removeEventListener('mce:open-cookie-settings', openSettings);
  }, [consent]);

  const save = (next) => {
    setConsent(persistConsent(next));
    setSettingsOpen(false);
  };

  return (
    <>
      {!consent && (
        <aside className="cookie-consent-banner" role="dialog" aria-modal="false" aria-labelledby="cookie-consent-title">
          <div className="cookie-consent-icon"><Cookie /></div>
          <div className="cookie-consent-copy">
            <h2 id="cookie-consent-title">Votre confidentialité, votre choix</h2>
            <p>Les cookies peuvent servir à cibler la publicité (Marketing), analyser les performances (Statistiques), personnaliser l’affichage (Préférences) et maintenir la connexion (Gestion de session). Vous pouvez les accepter, les refuser ou personnaliser votre choix.</p>
            <Link to="/politique-de-confidentialite">Consulter la politique de confidentialité</Link>
          </div>
          <div className="cookie-consent-actions">
            <button type="button" className="cookie-button-secondary" onClick={() => save({ analytics: false, marketing: false, personalization: false })}>Tout refuser</button>
            <button type="button" className="cookie-button-settings" onClick={() => setSettingsOpen(true)}><SlidersHorizontal /> Personnaliser</button>
            <button type="button" className="cookie-button-primary" onClick={() => save({ analytics: true, marketing: true, personalization: true })}>Tout accepter</button>
          </div>
        </aside>
      )}

      {settingsOpen && (
        <div className="cookie-settings-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSettingsOpen(false); }}>
          <section className="cookie-settings-dialog" role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title">
            <button type="button" className="cookie-settings-close" onClick={() => setSettingsOpen(false)} aria-label="Fermer"><X /></button>
            <header><ShieldCheck /><div><span>PRÉFÉRENCES</span><h2 id="cookie-settings-title">Gérer mes cookies</h2></div></header>
            <p>Les cookies nécessaires restent actifs pour mémoriser vos choix et assurer le fonctionnement du site.</p>
            <div className="cookie-preference-list">
              <article><div><b>Gestion de session — nécessaires</b><small>Maintenir la connexion lorsqu’un espace connecté est disponible et mémoriser vos choix de consentement.</small></div><span className="cookie-always-active">Toujours actifs</span></article>
              <article><div><b>Statistiques</b><small>Analyser les performances et comprendre l’utilisation du site.</small></div><label className="cookie-switch"><input type="checkbox" aria-label="Autoriser les statistiques" checked={preferences.analytics} onChange={(event) => setPreferences((current) => ({ ...current, analytics: event.target.checked }))} /><span /></label></article>
              <article><div><b>Marketing</b><small>Cibler la publicité et mesurer les campagnes publicitaires.</small></div><label className="cookie-switch"><input type="checkbox" aria-label="Autoriser le marketing" checked={preferences.marketing} onChange={(event) => setPreferences((current) => ({ ...current, marketing: event.target.checked }))} /><span /></label></article>
              <article><div><b>Préférences</b><small>Personnaliser l’affichage selon vos choix.</small></div><label className="cookie-switch"><input type="checkbox" aria-label="Autoriser la personnalisation" checked={preferences.personalization} onChange={(event) => setPreferences(current => ({ ...current, personalization: event.target.checked }))} /><span /></label></article>
            </div>
            <div className="cookie-settings-actions">
              <button type="button" className="cookie-button-secondary" onClick={() => save({ analytics: false, marketing: false, personalization: false })}>Tout refuser</button>
              <button type="button" className="cookie-button-primary" onClick={() => save(preferences)}>Enregistrer mes choix</button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
